import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { Cpu, Zap, Shield, BookOpen, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

export default function AISettings() {
  const { user } = useAuth();
  const { t } = useI18n();
  const tenantId = user?.tenant_id;
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/tenants/${tenantId}/settings/ai`).then(r => setSettings(r.data)).catch(() => {});
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/settings/ai`, settings);
      toast.success(t.save);
    } catch { toast.error(t.error); }
    finally { setSaving(false); }
  };

  if (!settings) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-7 h-7 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
    </div>
  );

  const providerOk = settings.provider && settings.model;
  const embeddingOk = settings.embeddings_available;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[900px] mx-auto animate-fade-in-up" data-testid="ai-settings-page">
      <div>
        <h2 className="font-heading font-bold text-2xl text-white">{t.aiSettingsTitle}</h2>
        <p className="text-sm text-[#a1a1aa] mt-1">{t.aiSettingsDesc}</p>
      </div>

      {/* Provider Status */}
      <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
        <h3 className="font-heading font-semibold text-sm text-white flex items-center gap-2 mb-4">
          <Cpu className="w-4 h-4 text-[#EF694C]" /> {t.aiProviderStatus}
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-[#09090b] rounded-lg p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              {providerOk ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-[#F43F5E]" />}
              <span className="text-xs text-[#71717a]">LLM</span>
            </div>
            <p className="text-sm text-white font-medium">{settings.provider || '-'}</p>
            <p className="text-xs text-[#52525b] font-mono">{settings.model || '-'}</p>
          </div>
          <div className="bg-[#09090b] rounded-lg p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              {embeddingOk ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />}
              <span className="text-xs text-[#71717a]">{t.embeddings}</span>
            </div>
            <p className="text-sm text-white font-medium">{settings.current_embedding?.provider || 'chromadb_builtin'}</p>
            <p className="text-xs text-[#52525b] font-mono">{settings.current_embedding?.model || '-'}</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-[#121214] border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="font-heading font-semibold text-sm text-white flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#F59E0B]" /> {t.aiFeatures}
        </h3>
        {[
          { key: 'ai_enabled', label: t.enableAI, desc: t.enableAIDesc },
          { key: 'auto_reply_enabled', label: t.autoReply, desc: t.autoReplyDesc },
          { key: 'escalation_detection', label: t.escalationDetection, desc: t.escalationDetectionDesc },
        ].map(item => (
          <div key={item.key} className="flex items-center justify-between p-3 bg-[#09090b] rounded-lg border border-white/5">
            <div>
              <p className="text-sm text-white font-medium">{item.label}</p>
              <p className="text-xs text-[#71717a]">{item.desc}</p>
            </div>
            <button onClick={() => setSettings(p => ({ ...p, [item.key]: !p[item.key] }))} data-testid={`toggle-${item.key}`}
              className={`w-11 h-6 rounded-full relative transition-colors ${settings[item.key] ? 'bg-[#EF694C]' : 'bg-[#27272a]'}`}>
              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow ${settings[item.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </div>
        ))}
      </div>

      {/* Response Config */}
      <div className="bg-[#121214] border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="font-heading font-semibold text-sm text-white">{t.responseConfig}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.toneOfVoice}</label>
            <select value={settings.tone || 'professional'} onChange={e => setSettings(p => ({ ...p, tone: e.target.value }))} data-testid="tone-select"
              className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]">
              {['professional', 'friendly', 'formal', 'casual', 'empathetic'].map(v => (
                <option key={v} value={v}>{t[v]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.confidenceThreshold}: {settings.confidence_threshold || 0.7}</label>
            <input type="range" min="0.3" max="0.95" step="0.05" value={settings.confidence_threshold || 0.7}
              onChange={e => setSettings(p => ({ ...p, confidence_threshold: parseFloat(e.target.value) }))} data-testid="confidence-slider"
              className="w-full accent-[#EF694C]" />
            <div className="flex justify-between text-[10px] text-[#52525b]"><span>{t.moreReplies}</span><span>{t.onlyCertain}</span></div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.systemInstructions}</label>
          <textarea value={settings.system_prompt || ''} onChange={e => setSettings(p => ({ ...p, system_prompt: e.target.value }))}
            rows={3} placeholder={t.systemInstructionsPlaceholder} data-testid="system-prompt"
            className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all resize-none" />
        </div>
      </div>

      {/* RAG */}
      <div className="bg-[#121214] border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="font-heading font-semibold text-sm text-white flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#3B82F6]" /> {t.knowledgeRetrieval}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.topKResults}: {settings.retrieval_top_k || 5}</label>
            <input type="range" min="1" max="15" value={settings.retrieval_top_k || 5}
              onChange={e => setSettings(p => ({ ...p, retrieval_top_k: parseInt(e.target.value) }))} data-testid="topk-slider"
              className="w-full accent-[#EF694C]" />
            <div className="flex justify-between text-[10px] text-[#52525b]"><span>{t.focused}</span><span>{t.broad}</span></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.minScoreThreshold}: {settings.retrieval_score_threshold || 0.3}</label>
            <input type="range" min="0.1" max="0.9" step="0.05" value={settings.retrieval_score_threshold || 0.3}
              onChange={e => setSettings(p => ({ ...p, retrieval_score_threshold: parseFloat(e.target.value) }))} data-testid="score-slider"
              className="w-full accent-[#EF694C]" />
            <div className="flex justify-between text-[10px] text-[#52525b]"><span>{t.includeMore}</span><span>{t.highRelevance}</span></div>
          </div>
        </div>
      </div>

      {/* Safety */}
      <div className="bg-[#121214] border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="font-heading font-semibold text-sm text-white flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#F43F5E]" /> {t.safetyPolicies}
        </h3>
        <div>
          <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.forbiddenTopics}</label>
          <input value={settings.forbidden_topics || ''} onChange={e => setSettings(p => ({ ...p, forbidden_topics: e.target.value }))}
            placeholder={t.forbiddenTopicsPlaceholder} data-testid="forbidden-topics"
            className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
        </div>
      </div>

      {/* Auto-reply warning */}
      {settings.auto_reply_enabled && (
        <div className="p-4 bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-semibold text-[#F59E0B]">{t.autoReplyWarning}</p>
          </div>
          <p className="text-sm text-[#d4d4d8]">
            {typeof t.autoReplyWarningDesc === 'function'
              ? t.autoReplyWarningDesc(settings.confidence_threshold || 0.7)
              : t.autoReplyWarningDesc}
          </p>
        </div>
      )}

      <button onClick={handleSave} disabled={saving} data-testid="save-ai-settings"
        className="h-11 px-6 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg text-sm shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all disabled:opacity-60">
        {saving ? t.saving : t.save}
      </button>
    </div>
  );
}
